/**
 * usePortfolioSavedFilterViews — persisted CRUD over named filter
 * combinations for the Portfolio Off-Chain page.
 *
 * Backed by `StorageService` under the key `portfolio_saved_filter_views`.
 * The shape is `Array<{ id: string; name: string; config: FilterConfig }>`.
 *
 * Created in Sprint V2.B4.1 (cf. `docs/v2/PORTFOLIO_OFFCHAIN_REFONTE.md`).
 */

import { useCallback, useState } from 'react';
import { StorageService } from '../services/storage';
import type { FilterConfig } from '../components/portfolio/PortfolioFilters';

const STORAGE_KEY = 'portfolio_saved_filter_views';

export interface SavedFilterView {
  /** Stable identifier — used as React key + lookup. Generated when
   *  `saveView` is called. */
  id: string;
  /** User-provided label rendered in the dropdown. Trimmed; non-empty. */
  name: string;
  /** Snapshot of the filter config at save time. */
  config: FilterConfig;
}

interface UseSavedFilterViewsApi {
  views: SavedFilterView[];
  /** Persists a new view. Returns the created view (for the caller to
   *  e.g. immediately apply its config). Trims `name` and rejects empty
   *  names by returning `null`. */
  saveView: (name: string, config: FilterConfig) => SavedFilterView | null;
  /** Returns the config matching `id`, or `null` if not found. */
  loadView: (id: string) => FilterConfig | null;
  /** Removes the view with `id`. No-op if not found. */
  deleteView: (id: string) => void;
  /** Renames the view with `id`. No-op if not found or new name empty. */
  renameView: (id: string, newName: string) => void;
}

function makeId(): string {
  // No need for crypto-strong randomness here — local-only, low cardinality.
  return `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function usePortfolioSavedFilterViews(): UseSavedFilterViewsApi {
  const [views, setViews] = useState<SavedFilterView[]>(() => {
    const saved = StorageService.get<SavedFilterView[]>(STORAGE_KEY, []);
    return Array.isArray(saved) ? saved : [];
  });

  // Functional state updaters so consecutive calls in the same render
  // cycle don't clobber each other via stale closures.
  const updateViews = useCallback(
    (mutator: (prev: SavedFilterView[]) => SavedFilterView[]) => {
      setViews((prev) => {
        const next = mutator(prev);
        StorageService.set(STORAGE_KEY, next);
        return next;
      });
    },
    [],
  );

  const saveView = useCallback(
    (name: string, config: FilterConfig): SavedFilterView | null => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      const view: SavedFilterView = {
        id: makeId(),
        name: trimmed,
        config: { ...config },
      };
      updateViews((prev) => [...prev, view]);
      return view;
    },
    [updateViews],
  );

  const loadView = useCallback(
    (id: string): FilterConfig | null => {
      const view = views.find((v) => v.id === id);
      return view ? { ...view.config } : null;
    },
    [views],
  );

  const deleteView = useCallback(
    (id: string) => updateViews((prev) => prev.filter((v) => v.id !== id)),
    [updateViews],
  );

  const renameView = useCallback(
    (id: string, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed) return;
      updateViews((prev) =>
        prev.map((v) => (v.id === id ? { ...v, name: trimmed } : v)),
      );
    },
    [updateViews],
  );

  return { views, saveView, loadView, deleteView, renameView };
}
